/**
 * **Pemilih modifier** — muncul saat produk yang diketuk punya grup modifier.
 *
 * ### ⭐ Kenapa dialog, padahal prinsipnya "hitung KETUKAN"
 * Prinsip itu berlaku untuk jalur normal: satu ketukan = satu item. Tapi produk BERMODIFIER punya
 * pertanyaan yang **harus** dijawab sebelum barangnya bisa dimasak — "tingkat pedas?", "es atau
 * tidak?". Menyimpannya untuk ditanya belakangan berarti tiket dapur berangkat tanpa jawaban, dan
 * yang menanggungnya adalah tamu yang menerima makanan yang salah.
 *
 * ⇒ Dialog hanya muncul untuk produk yang memang bermodifier. Produk polos tetap satu ketukan.
 *
 * ### ⛔ `minSelect` MEMAKSA
 * Tombol "Tambah" mati sampai tiap grup wajib terjawab. Server tetap menolak
 * (`validateModifierSelections`), tapi penolakan yang terjadi di depan tamu adalah penolakan yang
 * seharusnya tak perlu terjadi.
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ModifierGroup, ModifierSelectionInput } from '../api/client';

export interface ModifierPickerProps {
  readonly productName: string;
  readonly groups: readonly ModifierGroup[];
  readonly formatPrice: (canonical: string) => string;
  readonly onCancel: () => void;
  readonly onConfirm: (selections: readonly ModifierSelectionInput[]) => void;
  /**
   * ⭐⭐ Opsi yang sedang HABIS — `<groupCode>/<optionCode>` (K28 bagian modifier).
   *
   * ⛔ Bergaris-miring karena kode opsi unik per GRUP, bukan per tenant: meredupkan `PEDAS` di
   * SEMUA grup gara-gara satu grup kehabisan adalah mematikan pilihan yang masih tersedia.
   */
  readonly soldOut?: ReadonlySet<string>;
  /** ⭐ Tekan-tahan opsi ⇒ tandai/cabut HABIS. `undefined` ⇒ gerakannya mati. */
  readonly onHoldOption?: (groupCode: string, optionCode: string, name: string) => void;
}

/** Lama tekan sebelum dianggap "tekan-tahan" (ms) — sama dengan kisi menu. */
const HOLD_MS = 550;

export function ModifierPicker(p: ModifierPickerProps) {
  const { t } = useTranslation();

  /*
   * ⚠️ `fired` ada supaya ketukan yang menyusul SESUDAH tekan-tahan tak ikut MEMILIH opsinya —
   * tanpa itu, menandai topping habis juga memasukkannya ke pesanan.
   */
  const hold = useRef<{ timer: ReturnType<typeof setTimeout>; fired: boolean } | null>(null);
  const holdCancel = () => { if (hold.current !== null) clearTimeout(hold.current.timer); };
  const holdStart = (groupCode: string, optionCode: string, nama: string) => {
    if (p.onHoldOption === undefined) return;
    holdCancel();
    const st = {
      timer: setTimeout(() => { st.fired = true; p.onHoldOption?.(groupCode, optionCode, nama); },
        HOLD_MS),
      fired: false,
    };
    hold.current = st;
  };
  /** Kunci: `groupCode` → kode opsi terpilih. */
  const [pilih, setPilih] = useState<Record<string, string[]>>({});
  const [teks, setTeks] = useState<Record<string, string>>({});

  const kurang = useMemo(
    () => p.groups.filter((g) => (pilih[g.groupCode] ?? []).length < g.minSelect),
    [p.groups, pilih],
  );

  const toggle = (g: ModifierGroup, code: string) => {
    setPilih((s) => {
      const ada = s[g.groupCode] ?? [];
      if (ada.includes(code)) return { ...s, [g.groupCode]: ada.filter((c) => c !== code) };
      /*
       * ⚠️ `maxSelect = 1` berperilaku seperti radio: pilihan lama DIGANTI, bukan ditolak. Menolak
       * ketukan kedua memaksa kasir mencari cara membatalkan yang pertama — dua ketukan untuk satu
       * perubahan pikiran, berkali-kali sehari.
       */
      if (g.maxSelect === 1) return { ...s, [g.groupCode]: [code] };
      if (g.maxSelect > 0 && ada.length >= g.maxSelect) return s;
      return { ...s, [g.groupCode]: [...ada, code] };
    });
  };

  const konfirmasi = () => {
    const out: ModifierSelectionInput[] = [];
    for (const g of p.groups) {
      for (const code of pilih[g.groupCode] ?? []) {
        out.push({ groupCode: g.groupCode, optionCode: code });
      }
      const bebas = (teks[g.groupCode] ?? '').trim();
      /* ⛔ Teks bebas SELALU tanpa harga — teks yang bisa membawa harga adalah harga yang tak
         pernah disetujui siapa pun (§5d). */
      if (bebas !== '') out.push({ groupCode: g.groupCode, freeText: bebas });
    }
    p.onConfirm(out);
  };

  return (
    <div className="dialog modifier" role="dialog" aria-label={t('cashier.modifier.title')}
         data-testid="modifier-dialog">
      <h2>{p.productName}</h2>

      {p.groups.map((g) => (
        <section className="modifier-group" key={g.groupCode} data-testid={`mod-grup-${g.groupCode}`}>
          <h3>
            {g.groupName}
            {/* ⭐ Wajib/opsional disebut DI DEPAN, bukan ditemukan saat tombolnya tetap mati. */}
            {g.minSelect > 0 && (
              <span className="modifier-wajib" data-testid={`mod-wajib-${g.groupCode}`}>
                {t('cashier.modifier.required')}
              </span>
            )}
          </h3>

          <div className="modifier-options">
            {g.options.map((o) => {
              const on = (pilih[g.groupCode] ?? []).includes(o.code);
              const habis = p.soldOut?.has(`${g.groupCode}/${o.code}`) === true;
              return (
                <button
                  key={o.code}
                  type="button"
                  className={`modifier-option${on ? ' on' : ''}${habis ? ' habis' : ''}`}
                  data-testid={`mod-opsi-${o.code}`}
                  data-soldout={habis ? 'yes' : undefined}
                  aria-pressed={on}
                  /* ⛔ Yang habis TETAP TAMPIL, cuma redup — kalau disembunyikan, kasir kehilangan
                     benda yang harus ditekan-tahan untuk menyalakannya lagi (alasan yang sama
                     dengan kartu menu di K28). */
                  onClick={() => { if (!habis) toggle(g, o.code); }}
                  onPointerDown={() => { holdStart(g.groupCode, o.code, o.name); }}
                  onPointerUp={holdCancel}
                  onPointerLeave={holdCancel}
                  onPointerCancel={holdCancel}
                  onContextMenu={(e) => { e.preventDefault(); }}
                >
                  <span>{o.name}</span>
                  {habis && (
                    <b className="modifier-habis" data-testid={`mod-habis-${o.code}`}>
                      {t('cashier.soldOut.badge')}
                    </b>
                  )}
                  {/* ⚠️ Selisih harga ditampilkan apa adanya — modifier berbayar yang diam adalah
                      tagihan yang tak bisa dijelaskan kasir. */}
                  {o.priceDelta !== '0.0000' && (
                    <b data-testid={`mod-harga-${o.code}`}>+{p.formatPrice(o.priceDelta)}</b>
                  )}
                </button>
              );
            })}
          </div>

          {g.allowFreeText && (
            <input
              className="modifier-teks"
              placeholder={t('cashier.modifier.freeText')}
              data-testid={`mod-teks-${g.groupCode}`}
              maxLength={120}
              value={teks[g.groupCode] ?? ''}
              onChange={(e) => { setTeks((s) => ({ ...s, [g.groupCode]: e.target.value })); }}
            />
          )}
        </section>
      ))}

      <div className="dialog-actions">
        <button type="button" data-testid="mod-batal" onClick={p.onCancel}>
          {t('cashier.modifier.cancel')}
        </button>
        <button
          type="button"
          className="action-primary"
          data-testid="mod-tambah"
          disabled={kurang.length > 0}
          title={kurang.length > 0 ? t('cashier.modifier.stillRequired', {
            group: kurang[0]?.groupName ?? '',
          }) : undefined}
          onClick={konfirmasi}
        >
          {t('cashier.modifier.add')}
        </button>
      </div>
    </div>
  );
}
